import React from 'react';

import { MAP_CONSTANTS } from './mapConstants';
import { createPaddedBounds, calculateFlyToSpeed, shouldUseUserZoom } from './mapUtils';

export function updatePathSource(map: mapboxgl.Map | null, coordinates: [number, number][], inPipMode: boolean, sourceId = 'path'): void {
  if (!map) return;

  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
  if (source) {
    source.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: inPipMode ? [] : coordinates
      }
    });
  }
}

// FlyTo manager to handle queuing and prevent animation interruptions
class FlyToManager {
  private map: mapboxgl.Map | null = null;
  private isAnimating = false;
  private queuedTarget: { position: [number, number]; zoom?: number } | null = null;
  private moveEndHandler: (() => void) | null = null;
  private targetZoomRef: React.MutableRefObject<number> | null = null;
  private createdTimeRef: React.MutableRefObject<number> | null = null;
  private enabled = true;

  initialize(
    map: mapboxgl.Map,
    targetZoomRef: React.MutableRefObject<number>,
    createdTimeRef: React.MutableRefObject<number>
  ): void {
    this.map = map;
    this.targetZoomRef = targetZoomRef;
    this.createdTimeRef = createdTimeRef;

    this.moveEndHandler = () => {
      if (this.isAnimating) {
        this.isAnimating = false;

        // If there's a queued target, fly to it now
        if (this.queuedTarget && this.enabled) {
          const { position, zoom } = this.queuedTarget;
          this.queuedTarget = null;
          this.executeFlyTo(position, zoom);
        }
      }
    };

    map.on('moveend', this.moveEndHandler);
  }

  cleanup(): void {
    if (this.map && this.moveEndHandler) {
      this.map.off('moveend', this.moveEndHandler);
      this.moveEndHandler = null;
    }
    this.map = null;
    this.isAnimating = false;
    this.queuedTarget = null;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.queuedTarget = null;
      if (this.map && this.isAnimating) {
        this.map.stop();
      }
      this.isAnimating = false;
    }
  }

  queueFlyTo(target: [number, number], zoom?: number): void {
    if (!this.map || !this.enabled) return;

    this.queuedTarget = { position: target, zoom };

    if (!this.isAnimating) {
      this.queuedTarget = null;
      this.executeFlyTo(target, zoom);
    }
  }

  private executeFlyTo(target: [number, number], zoom?: number): void {
    if (!this.map || !this.targetZoomRef || !this.createdTimeRef) return;

    const bounds = this.map.getBounds();
    const shouldFly = !bounds || !createPaddedBounds(bounds, MAP_CONSTANTS.FLY_TO_BOUNDS_PADDING).contains(target);

    if (!shouldFly) {
      if (this.queuedTarget) {
        const { position, zoom: queuedZoom } = this.queuedTarget;
        this.queuedTarget = null;
        this.executeFlyTo(position, queuedZoom);
      }
      return;
    }

    const mapCenter = this.map.getCenter();
    const distance = Math.sqrt((mapCenter.lng - target[0]) ** 2 + (mapCenter.lat - target[1]) ** 2);

    const speed = calculateFlyToSpeed(distance);
    const canUseUserZoom = shouldUseUserZoom(this.createdTimeRef.current, MAP_CONSTANTS.USER_ZOOM_WAIT_MS);
    const targetZoomValue = zoom ?? (canUseUserZoom ? this.map.getZoom() : this.targetZoomRef.current);

    this.isAnimating = true;
    this.map.flyTo({
      center: target,
      zoom: targetZoomValue,
      essential: true,
      speed,
      curve: MAP_CONSTANTS.FLY_TO_CURVE
    });
  }
}

const flyToManagers = new WeakMap<mapboxgl.Map, FlyToManager>();

export function flyToPosition(
  map: mapboxgl.Map | null,
  target: [number, number],
  targetZoomRef: React.MutableRefObject<number>,
  createdTimeRef: React.MutableRefObject<number>,
  zoom?: number,
  enabled = true
): void {
  if (!map || !enabled) return;

  // Get or create flyTo manager for this map instance
  let manager = flyToManagers.get(map);
  if (!manager) {
    manager = new FlyToManager();
    manager.initialize(map, targetZoomRef, createdTimeRef);
    flyToManagers.set(map, manager);
  }

  manager.setEnabled(enabled);
  manager.queueFlyTo(target, zoom);
}

/**
 * Jump to a position without animation (instant positioning)
 * Used for initial positioning in PiP mode to avoid zoom animations
 * For initial positioning, always uses targetZoomRef to ensure zoom level matches expected value
 */
export function jumpToPosition(
  map: mapboxgl.Map | null,
  target: [number, number],
  targetZoomRef: React.MutableRefObject<number>,
  createdTimeRef: React.MutableRefObject<number>,
  zoom?: number
): void {
  if (!map) return;

  // For initial positioning, always use the target zoom to ensure consistency
  // This ensures the zoom level matches what was set during initialization
  const targetZoomValue = zoom ?? targetZoomRef.current;

  map.jumpTo({
    center: target,
    zoom: targetZoomValue
  });
}
